import type { ProcessedListingData } from "../../../@types/sharedSchema";

/**
 * The pure half of "בחר נכס"'s search (Trello #254) — shared by `NewListingPicker` (a contract's
 * property, a meeting's listing, a seller's "הנכס למכירה") and `ListingPicker` (creatives,
 * WhatsApp templates, makeovers). The server reads the term as an ADDRESS for both
 * (`searchTermMatch: 'address'`, server `addressSearchTerm.ts`); this decides what is asked and
 * how the two answers are laid out.
 */

/** Quote marks inside Hebrew names (gershayim / geresh, every script). Ignored, as on the server. */
const QUOTES = /["'`׳״‘’“”]/g;

/**
 * The filler words of three letters or more a realtor types before an address — the server's
 * `ADDRESS_FILLER_WORDS` (shorter ones are below the three-letter bar anyway). A term made only of
 * these says nothing about WHICH listing, and the server answers it with the newest listings.
 */
const FILLER_WORDS = new Set(["רחוב", "ברחוב", "ברח", "שכונת", "שכונה", "בשכונת", "דירה", "דירת", "קומה"]);

/**
 * Should the picker search the whole market (Yad2, Madlan, colleagues' listings) for this term?
 *
 * Only once the term names something: a word of three letters or more that is not filler. With
 * nothing typed the picker used to list the 30 most recently updated listings in the country
 * beside the realtor's own — every one of them irrelevant — and a two-letter word cannot be
 * answered by the server's index across 900k listings. The realtor's OWN listings are always
 * searched: they are few, and an empty term is how she browses them.
 */
export function shouldSearchMarket(term: string): boolean {
  const words = term
    .toLowerCase()
    .replace(QUOTES, "")
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => /^\p{L}{3,}$/u.test(word));
  return words.some((word) => !FILLER_WORDS.has(word));
}

type Hit = ProcessedListingData & { objectID?: string };

/**
 * The dropdown's list: her own listings first (ranked by the server), then the market's, without
 * the own listings the market query also returned — each with `id` set from the search's
 * `objectID`.
 */
export function mergePickerResults(own: readonly Hit[], market: readonly Hit[]): ProcessedListingData[] {
  const idOf = (hit: Hit): string => (hit.objectID ?? hit.id) as string;
  const ownIds = new Set(own.map(idOf));
  return [...own, ...market.filter((hit) => !ownIds.has(idOf(hit)))].map((hit) => ({ ...hit, id: idOf(hit) }));
}
